import { ArrowRight, ShieldAlert } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router";

import { supabase } from "../lib/supabase";
import { latestPackageForUser, workerDatabase } from "../offline/database";

export function PageHeading({
  eyebrow,
  title,
  intro,
}: {
  eyebrow: string;
  title: string;
  intro: string;
}) {
  return (
    <header className="mb-7">
      <p className="mb-2 text-sm font-extrabold uppercase tracking-wide text-sky-deep">
        {eyebrow}
      </p>
      <h1 className="text-3xl font-extrabold tracking-tight md:text-4xl">
        {title}
      </h1>
      <p className="mt-3 max-w-2xl text-lg text-muted">{intro}</p>
    </header>
  );
}

export function HomePage() {
  return (
    <div className="page">
      <PageHeading
        eyebrow="Today"
        title="You can work independently"
        intro="Teo may rest or communicate minimally. Continue with the shift unless an activity genuinely needs his involvement."
      />
      <section className="card bg-sky-soft p-6">
        <p className="text-sm font-bold text-sky-deep">Communication status</p>
        <h2 className="mt-1 text-2xl font-extrabold">
          Resting, work independently
        </h2>
        <p className="mt-2 text-muted">
          Avoid entering Teo's room. Use normal text messages for anything that
          needs a response.
        </p>
      </section>
      <section className="mt-5 grid gap-4 md:grid-cols-2">
        <article className="card p-6">
          <p className="text-sm font-bold text-sky-deep">Next assigned shift</p>
          <h2 className="mt-1 text-xl font-bold">
            Your brief will appear here
          </h2>
          <p className="mt-2 text-muted">
            Assigned dates are visible before a brief is published. Instructions
            appear after publication.
          </p>
          <Link className="button-primary mt-5" to="/shift">
            Open my shift <ArrowRight size={18} aria-hidden />
          </Link>
        </article>
        <article className="card p-6">
          <p className="text-sm font-bold text-warning">Unexpected crisis?</p>
          <h2 className="mt-1 text-xl font-bold">Use emergency guidance</h2>
          <p className="mt-2 text-muted">
            If Teo is expected to be awake, is alone and does not respond to
            arrival texts, follow the emergency flow.
          </p>
          <Link className="button-secondary mt-5" to="/emergency">
            Emergency guide <ShieldAlert size={18} aria-hidden />
          </Link>
        </article>
      </section>
    </div>
  );
}

export function OnboardingPage() {
  const [onboarding, setOnboarding] = useState<{
    approved_at: string | null;
    completed_at: string | null;
    revision_number: number;
  } | null>(null);
  const [message, setMessage] = useState("Checking your onboarding…");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    void supabase
      .from("onboarding")
      .select("completed_at, approved_at, revision_number")
      .maybeSingle()
      .then(({ data, error }) => {
        if (!active) return;
        setOnboarding(data);
        setMessage(
          error || !data
            ? "Ask Teo or Cathy to prepare your onboarding record."
            : data.approved_at
              ? "Your onboarding is approved."
              : data.completed_at
                ? "Completed—waiting for Teo or Cathy to approve it."
                : "Review the required information, then confirm completion.",
        );
      });
    return () => {
      active = false;
    };
  }, []);

  async function complete() {
    if (!onboarding) return;
    setSaving(true);
    const { data, error } = await supabase.rpc("complete_my_onboarding", {
      p_expected_revision: onboarding.revision_number,
    });
    setSaving(false);
    if (error) {
      setMessage("Your onboarding state changed. Reload and review it again.");
      return;
    }
    setOnboarding(data);
    setMessage("Completed—waiting for Teo or Cathy to approve it.");
  }

  return (
    <div className="page">
      <PageHeading
        eyebrow="Getting started"
        title="Worker onboarding"
        intro="Complete every required item before assigned shift briefs become available."
      />
      <div className="card p-6" aria-live="polite">
        <p className="font-bold">
          Your onboarding status is checked by the server.
        </p>
        <p className="mt-2 text-muted">{message}</p>
        {onboarding && !onboarding.completed_at && !onboarding.approved_at ? (
          <button
            className="button-primary mt-5"
            type="button"
            disabled={saving}
            onClick={() => void complete()}
          >
            {saving ? "Confirming…" : "I have completed onboarding"}
          </button>
        ) : null}
      </div>
    </div>
  );
}

export function RecentShiftPage() {
  const [recent, setRecent] = useState<{
    scheduled_start: string;
    finished_at: string | null;
    handovers: Array<{ summary: string; submitted_at: string }>;
  } | null>(null);

  useEffect(() => {
    let active = true;
    void (async () => {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) return;
      if (!navigator.onLine) {
        await workerDatabase.purgeExpired();
        const cached = await latestPackageForUser(userData.user.id);
        const payload = cached?.payload as
          { recentShift?: typeof recent } | undefined;
        if (active) setRecent(payload?.recentShift ?? null);
        return;
      }
      const { data } = await supabase
        .from("shifts")
        .select(
          "scheduled_start, finished_at, handovers(summary, submitted_at)",
        )
        .eq("status", "archived")
        .order("finished_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (active) setRecent(data);
    })();
    return () => {
      active = false;
    };
  }, []);

  return (
    <div className="page">
      <PageHeading
        eyebrow="Read-only"
        title="My recent shift"
        intro="Your most recently finished shift appears here without exposing another worker's records."
      />
      {recent ? (
        <article className="card p-6">
          <h2 className="text-xl font-bold">
            {new Date(recent.scheduled_start).toLocaleString("en-AU")}
          </h2>
          <p className="mt-2 text-sm text-muted">
            Finished{" "}
            {recent.finished_at
              ? new Date(recent.finished_at).toLocaleString("en-AU")
              : "recently"}
          </p>
          {recent.handovers.map((handover) => (
            <p key={handover.submitted_at} className="mt-4 whitespace-pre-wrap">
              {handover.summary}
            </p>
          ))}
        </article>
      ) : (
        <div className="card p-6 text-muted">No recent shift is available.</div>
      )}
    </div>
  );
}

export function NotFoundPage() {
  return (
    <main className="page">
      <PageHeading
        eyebrow="Page not found"
        title="That page is not available"
        intro="The link may have expired or you may not have access."
      />
      <Link className="button-primary" to="/home">
        Return home
      </Link>
    </main>
  );
}
